<template>
    <div
        id="stats"
        v-dark
        class="stats"
    >
        <StatsBanner v-if="examMetadata" :key="answerKey" />
        <ReadinessReportBanner
            v-if="!isLoading"
            :is-dark-mode="isDarkMode"
            class="stats__readiness-report-banner"
            @isBannerVisible="updateIsShowingReadinessReportBanner"
        />
        <ExamDateBanner
            v-if="!isLoading && canSeeExamDateBanner"
            :is-dark-mode="isDarkMode"
            class="stats__exam-date-banner"
            @close="closeExamDateBanner"
        />
        <PocketButton
            v-if="!isLoading && breakpoint === 'black-bear' && additionalQuestionCount && !hasPremiumAccess"
            :is-dark-mode="isDarkMode"
            class="stats__mobile-cta"
            :is-loading="isLoading"
            @click="openUpgradeModal"
        >
            Get {{ additionalQuestionCount }} more questions with Premium
        </PocketButton>
        <SubjectInsights />
        <div class="stats__2-column">
            <div class="stats__2-column-left">
                <QuizScores class="stats__quiz-scores" />
                <StudyProgress class="stats__study-progress" />
            </div>
            <div class="stats__2-column-right">
                <StudyActivity class="stats__study-activity" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import StatsBanner from '@/components/Stats/Banner.vue'
import SubjectInsights from '@/components/SubjectInsights.vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import QuizScores from '@/components/Stats/QuizScores.vue'
import StudyActivity from '@/components/Stats/StudyActivity.vue'
import StudyProgress from '@/components/Stats/StudyProgress.vue'
import ExamDateBanner from '@/components/ExamDateBanner.vue'
import ReadinessReportBanner from '@/components/ReadinessReport/ReadinessReportBanner.vue'
import { screenModule } from '@/store/screen/module'
import { subscriptionModule } from '@/store/subscription/module'
import { upgradeModule } from '@/store/upgrade/module'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'

const { Button: PocketButton } = UIKit

const isLoading = ref(true)
const isShowingReadinessReportBanner = ref<boolean | null>(null)

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const examMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const answerKey = computed(() => {
    // keep track of most recent answer serial because if it changes then we reload the Stats Banner
    const answers = Object.values(quizModule.getters.getLatestAnswers({ questionFilter: 'accessible' }))
    const answer = answers[0]
    return `${answer?.questionSerial}_${answers.length}`
})

const additionalQuestionCount = computed(() => {
    if (examMetadata.value) {
        // subtract archived and free questions
        return (examMetadata.value?.itemCount || 0)
            - (examMetadata.value?.archivedCount || 0) - (examMetadata.value?.specialQuestions || 0)
    }
    return 0
})

const hasPremiumAccess = computed(() => {
    const currentExam = examMetadataModule.getters.getCurrentExamMetadata()
    return subscriptionModule.getters.getSubscriptionForExamId(currentExam?.objectId)
})

const canSeeExamDateBanner = computed(() => {
    // Hide the exam date banner if we're showing the readiness banner or still loading the readiness banner
    if (isShowingReadinessReportBanner.value || isShowingReadinessReportBanner.value === null) {
        return false
    }

    return userExamMetadataModule.getters.getCanSeeExamDateBanner('Stats')
})

onMounted(async () => {
    await Promise.all([
        examMetadataModule.actions.fetchExamMetadata(),
        userModule.actions.fetchUserData(),
    ])

    const statsEl = document.getElementById('stats')
    if (statsEl) {
        statsEl.scrollIntoView()
        const headEl = document.querySelector('.head')
        headEl?.scrollIntoView()    // For mobile, make sure we can see the header menu
    }

    isLoading.value = false
})

const openUpgradeModal = () => {
    upgradeModule.actions.openUpgradeModal()
}

const closeExamDateBanner = () => {
    userModule.actions.markMessageSeen('examDateBanner')
}

const updateIsShowingReadinessReportBanner = (value: boolean) => {
    isShowingReadinessReportBanner.value = value
}
</script>

<style lang="scss" scoped>
.stats {
    background: $gray-background;
    padding: 0 0 50px;

    &--dark {
        background: $charcoal;
    }

    &__readiness-report-banner,
    &__exam-date-banner {
        width: 975px;
        margin: 21px auto -8px;

        @include breakpoint(brown-bear) {
            width: 725px;
        }

        @include breakpoint(black-bear) {
            width: calc(100% - 36px);
            margin-top: 16px;
            margin-bottom: -4px;
        }
    }

    &__mobile-cta {
        width: calc(100% - 36px);
        margin-left: 18px;
        margin-top: 30px;
    }

    &__2-column {
        display: flex;
        justify-content: space-between;
        max-width: 974px;
        margin: 0 auto;

        @include breakpoint(brown-bear) {
            display: block;
            margin: 0 auto;
            max-width: 725px;
        }

        @include breakpoint(brown-bear) {
            box-sizing: border-box;
        }

        @include breakpoint(black-bear) {
            padding: 0 18px;
        }
    }

    &__2-column-left {
        width: 542px;

        @include breakpoint(brown-bear) {
            width: 100%;
        }
    }

    &__2-column-right {
        width: 420px;

        @include breakpoint(brown-bear) {
            width: 100%;
        }
    }

    &__quiz-scores {
        margin-bottom: 12px;

        @include breakpoint(brown-bear) {
            margin-bottom: 16px;
        }

        @include breakpoint(black-bear) {
            margin-bottom: 20px;
        }
    }

    &__study-progress {
        @include breakpoint(brown-bear) {
            margin-bottom: 16px;
        }

        @include breakpoint(black-bear) {
            margin-bottom: 20px;
        }
    }
}
</style>
